import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { RadioButton } from 'primereact/radiobutton';
import classes from '../SelectContractType/selectContractType.module.scss';
import { useStore } from 'hooks/useStore';

const SelectProductType = observer(() => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { BidAnalysisStore } = useStore();

  // Read URL params and restore state
  useEffect(() => {
    const typeFromUrl = searchParams.get('type') as 'mas' | 'nc' | null;
    const oppIdFromUrl = searchParams.get('oppId');
    const productTypeFromUrl = searchParams.get('productType') as 'direct' | 'indirect' | null;

    // Restore contract type
    if (typeFromUrl && !BidAnalysisStore.selectedContractType) {
      BidAnalysisStore.setContractType(typeFromUrl);
    }

    // Restore product type
    if (productTypeFromUrl && !BidAnalysisStore.selectedProductType) {
      BidAnalysisStore.setProductType(productTypeFromUrl);
    }

    // Restore opportunity if we have oppId
    if (oppIdFromUrl && BidAnalysisStore.opportunityIterations.length > 0) {
      const opportunity = BidAnalysisStore.opportunityIterations.find(
        (opp) => opp.id.toString() === oppIdFromUrl
      );
      if (opportunity && !BidAnalysisStore.selectedOpportunity) {
        BidAnalysisStore.setSelectedOpportunity(opportunity);
      }
    }

    // Redirect if previous steps not completed
    if (!BidAnalysisStore.selectedContractType && !typeFromUrl) {
      console.error('Previous steps incomplete, redirecting');
      navigate('/bdportal/bid-analysis/wizard/contract-type');
      return;
    }

    if (!BidAnalysisStore.selectedOpportunity && !oppIdFromUrl) {
      console.error('No opportunity selected, redirecting');
      navigate(`/bdportal/bid-analysis/wizard/opportunity${typeFromUrl ? `?type=${typeFromUrl}` : ''}`);
      return;
    }

    // Load opportunities if needed
    const contractType = BidAnalysisStore.selectedContractType || typeFromUrl;
    if (contractType && BidAnalysisStore.opportunityIterations.length === 0) {
      BidAnalysisStore.fetchOpportunityIterations(contractType).then(() => {
        // After loading, restore opportunity from URL
        if (oppIdFromUrl) {
          const opportunity = BidAnalysisStore.opportunityIterations.find(
            (opp) => opp.id.toString() === oppIdFromUrl
          );
          if (opportunity) {
            BidAnalysisStore.setSelectedOpportunity(opportunity);
          }
        }
      });
    }
  }, [searchParams, BidAnalysisStore, navigate]);

  // Static product types - no need for mock data
  const productTypes = [
    {
      id: 'direct' as const,
      name: 'Direct Product',
      description: 'In-house packaging and distribution with full revenue recognition',
      features: [
        'In-house packaging and distribution',
        'Full revenue recognition',
        'Supplier constraint considerations',
        'Manufacturing capacity analysis',
        'Supply chain risk assessment',
      ],
    },
    {
      id: 'indirect' as const,
      name: 'Indirect Product',
      description: 'Manufacturer handles distribution with commission-based revenue model',
      features: [
        'Manufacturer handles distribution',
        'Commission-based revenue model',
        'Market access focused',
        'Partner relationship management',
        'Simplified supply chain analysis',
      ],
    },
  ];

  const handleProductTypeSelect = (type: 'direct' | 'indirect') => {
    BidAnalysisStore.setProductType(type);
    const typeFromUrl = searchParams.get('type');
    const oppIdFromUrl = searchParams.get('oppId');
    // Update URL params
    setSearchParams({
      type: typeFromUrl || '',
      oppId: oppIdFromUrl || '',
      productType: type,
    });
  };

  const handleNext = () => {
    if (BidAnalysisStore.selectedProductType) {
      const typeFromUrl = searchParams.get('type');
      const oppIdFromUrl = searchParams.get('oppId');
      navigate(
        `/bdportal/bid-analysis/wizard/confirmation?type=${typeFromUrl}&oppId=${oppIdFromUrl}&productType=${BidAnalysisStore.selectedProductType}`
      );
    }
  };

  const handleBack = () => {
    const typeFromUrl = searchParams.get('type');
    const oppIdFromUrl = searchParams.get('oppId');
    navigate(`/bdportal/bid-analysis/wizard/opportunity?type=${typeFromUrl}&oppId=${oppIdFromUrl}`);
  };

  return (
    <div className={classes.wizardContainer}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerContent}>
          <div className={classes.logo}>
            <span>gsms</span>
          </div>
          <h1>Create New Bid Analysis</h1>
        </div>
      </div>

      <div className={classes.contentContainer}>
        {/* Progress Stepper */}
        <div className={classes.stepperContainer}>
          <div className={classes.stepper}>
            <div className={classes.stepLine}></div>
            <div className={classes.stepLineProgress} style={{ width: '66%' }}></div>

            <div className={`${classes.step}`}>
              <div className={`${classes.stepCircle} ${classes.stepCircleCompleted}`}>
                <i className="pi pi-check"></i>
              </div>
              <div className={classes.stepLabel}>Contract Type</div>
            </div>

            <div className={`${classes.step}`}>
              <div className={`${classes.stepCircle} ${classes.stepCircleCompleted}`}>
                <i className="pi pi-check"></i>
              </div>
              <div className={classes.stepLabel}>Opportunity List</div>
            </div>

            <div className={`${classes.step} ${classes.stepActive}`}>
              <div className={`${classes.stepCircle} ${classes.stepCircleActive}`}>3</div>
              <div className={classes.stepLabel}>Product Type</div>
            </div>

            <div className={classes.step}>
              <div className={classes.stepCircle}>4</div>
              <div className={classes.stepLabel}>Confirmation</div>
            </div>
          </div>
        </div>

        {/* Page Title */}
        <h2 className={classes.pageTitle}>Select Product Type</h2>
        <p className={classes.pageSubtitle}>
          Specify whether this is a direct or indirect product to determine the appropriate revenue model and analysis
          requirements.
        </p>

        {/* Product Type Selection */}
        <div className={classes.productTypeSection}>
          <div className={classes.directIndirectGrid}>
            {productTypes.map((productType) => (
              <Card
                key={productType.id}
                className={`${classes.directIndirectCard} ${
                  BidAnalysisStore.selectedProductType === productType.id ? classes.contractTypeCardSelected : ''
                }`}
                onClick={() => handleProductTypeSelect(productType.id)}
              >
                <div className={classes.directIndirectHeader}>
                  <RadioButton
                    inputId={productType.id}
                    value={productType.id}
                    checked={BidAnalysisStore.selectedProductType === productType.id}
                    onChange={() => handleProductTypeSelect(productType.id)}
                  />
                  <h4>{productType.name}</h4>
                </div>
                <p className={classes.directIndirectDescription}>{productType.description}</p>

                <ul className={classes.directIndirectFeatures}>
                  {productType.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className={classes.actionButtons}>
          <Button label="Back" icon="pi pi-arrow-left" onClick={handleBack} outlined />
          <Button
            label="Next"
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={handleNext}
            disabled={!BidAnalysisStore.selectedProductType}
          />
        </div>
      </div>
    </div>
  );
});

export default SelectProductType;
